import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Container, Pagination, Table } from 'react-bootstrap';
import { PencilFill, PersonPlusFill, TrashFill } from 'react-bootstrap-icons';
import { CursosService } from '../../../../service/cursos.service';
import { Curso } from '../../../../service/types/cursos.types';
import usePagination from '../../../../service/utils/usePagination';

interface CursosListProps {
  onSelectCurso: (curso: Curso) => void;
  onManageAsignaciones?: (curso: Curso) => void;
  onCreateNew?: () => void;
}

export default function CursosList({ onSelectCurso, onManageAsignaciones, onCreateNew }: CursosListProps) {
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentPage, totalPages, nextPage, prevPage, currentData } = usePagination(cursos, 10);

  const load = async () => {
    setLoading(true);
    try {
      const data = await CursosService.findAll();
      setCursos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (curso: Curso) => {
    if (!window.confirm(`¿Eliminar el curso "${curso.titulo}"? Esta acción no se puede deshacer.`)) return;
    await CursosService.remove(curso.id);
    await load();
  };

  return (
    <Container fluid className="p-0">
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Cursos</h5>
          {onCreateNew && (
            <Button size="sm" variant="light" onClick={onCreateNew}>
              + Nuevo curso
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Título</th>
                <th>Fecha límite</th>
                <th>Duración aproximada</th>
                <th>Vigencia</th>
                <th className="text-center">Trabajadores inscritos</th>
                <th className="text-center">Completados</th>
                <th className="text-center">No completados</th>
                <th>Estado</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentData().map((curso) => (
                <tr key={curso.id} className={curso.todosCompletados ? 'table-secondary text-muted' : undefined}>
                  <td>{curso.titulo}</td>
                  <td>{curso.fechaLimite}</td>
                  <td>{curso.duracion != null ? `${curso.duracion} h` : '—'}</td>
                  <td>{curso.vigencia != null ? `${curso.vigencia} meses` : 'Indefinida'}</td>
                  <td className="text-center">{curso.inscritos ?? 0}</td>
                  <td className="text-center">{curso.completados ?? 0}</td>
                  <td className="text-center">{curso.noCompletados ?? 0}</td>
                  <td>
                    <Badge bg={curso.activo ? 'success' : 'secondary'}>
                      {curso.activo ? 'Activo' : 'Inactivo'}
                    </Badge>{' '}
                    {curso.todosCompletados && <Badge bg="dark">Todos completaron</Badge>}
                  </td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <Button variant="outline-primary" size="sm" onClick={() => onSelectCurso(curso)}>
                        <PencilFill className="me-1" /> Editar
                      </Button>
                      {onManageAsignaciones && (
                        <Button variant="outline-info" size="sm" onClick={() => onManageAsignaciones(curso)}>
                          <PersonPlusFill className="me-1" /> Asignar
                        </Button>
                      )}
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(curso)}>
                        <TrashFill className="me-1" /> Eliminar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && cursos.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No hay cursos creados
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev onClick={prevPage} disabled={currentPage === 1} />
              <Pagination.Item disabled>{`${currentPage} / ${totalPages}`}</Pagination.Item>
              <Pagination.Next onClick={nextPage} disabled={currentPage === totalPages} />
            </Pagination>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
